import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  GraduationCap,
  ArrowRight,
  Globe,
  ExternalLink,
  BookOpen,
  Building2,
} from "lucide-react";

const Footer = () => {
  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "About College", path: "/about-college" },
    { name: "Facilities", path: "/facilities" },
    { name: "Activity", path: "/activity" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact", path: "/contact" },
  ];

  const importantLinks = [
    { name: "Principal Message", path: "/principal-message" },
    { name: "College Facilities", path: "/facilities" },
    { name: "Transfer Certificate", path: "/student/tc" },
    { name: "Enquiry Form", path: "/enquiry" },
    { name: "Terms & Conditions", path: "/terms" },
    { name: "Privacy Policy", path: "/privacy-policy" },
  ];

  return (
    <footer className="relative w-full bg-[#061641] text-white overflow-hidden">
      <div className="absolute top-0 right-0 w-[280px] h-[280px] bg-[#FFD700]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[240px] h-[240px] bg-white/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 w-full px-6 lg:px-12 py-8 md:py-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-20 h-20 rounded-full flex items-center justify-center">
                  <img
                    src="/logo.png"
                    alt="College Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              <h3 className="text-lg font-black text-white mb-2">
                Pt. Shrinivas Dwivedi Mahavidyalaya
              </h3>

              <p className="text-[#FFD700] text-xs font-bold mb-3">
                Arangi, Chandauli, Uttar Pradesh
              </p>

              <p className="text-white/70 text-sm leading-relaxed mb-4">
                A reputed higher education institution committed to quality
                education, discipline, values, and overall student development.
              </p>

              <div className="bg-white/8 border border-white/10 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <GraduationCap size={15} className="text-[#FFD700]" />
                  <p className="text-[#FFD700] text-xs font-black">
                    Affiliated To
                  </p>
                </div>

                <p className="text-white/75 text-xs leading-relaxed">
                  Mahatma Gandhi Kashi Vidyapith, Varanasi
                </p>
              </div>

              <div className="flex gap-3 mt-4">
                <Link
                  to="/"
                  className="w-9 h-9 rounded-lg bg-white/10 text-[#FFD700] flex items-center justify-center hover:bg-[#FFD700] hover:text-[#071A52] transition-all"
                  aria-label="Website"
                >
                  <Globe size={16} />
                </Link>

                <a
                  href="mailto:psndwivedi@gmail.com"
                  className="w-9 h-9 rounded-lg bg-white/10 text-[#FFD700] flex items-center justify-center hover:bg-[#FFD700] hover:text-[#071A52] transition-all"
                  aria-label="Email"
                >
                  <Mail size={16} />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-black text-white mb-3">
                Quick Links
              </h3>

              <div className="w-12 h-1 bg-[#FFD700] rounded-full mb-4"></div>

              <ul className="space-y-2.5">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="group inline-flex items-center gap-2 text-white/70 hover:text-[#FFD700] text-sm font-semibold transition-all"
                    >
                      <span className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center group-hover:bg-[#FFD700] group-hover:text-[#071A52] transition-all">
                        <ArrowRight size={12} />
                      </span>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-black text-white mb-3">
                Important Links
              </h3>

              <div className="w-12 h-1 bg-[#FFD700] rounded-full mb-4"></div>

              <ul className="space-y-2.5">
                {importantLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="group inline-flex items-center gap-2 text-white/70 hover:text-[#FFD700] text-sm font-semibold transition-all"
                    >
                      <span className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center group-hover:bg-[#FFD700] group-hover:text-[#071A52] transition-all">
                        <ExternalLink size={12} />
                      </span>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-5">
                <Link
                  to="/enquiry"
                  className="inline-flex items-center justify-center gap-2 bg-[#FFD700] text-[#071A52] px-5 py-2.5 rounded-lg text-xs font-black hover:bg-white transition-all"
                >
                  Enquiry Form
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-black text-white mb-3">
                Contact Details
              </h3>

              <div className="w-12 h-1 bg-[#FFD700] rounded-full mb-4"></div>

              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FFD700] text-[#071A52] flex items-center justify-center shrink-0">
                    <MapPin size={16} />
                  </div>

                  <p className="text-white/70 text-xs leading-relaxed">
                    Village & Post Arangi, District Chandauli, Uttar Pradesh -
                    232110
                  </p>
                </div>

                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FFD700] text-[#071A52] flex items-center justify-center shrink-0">
                    <Phone size={16} />
                  </div>

                  <div>
                    <a
                      href="tel:+919451887217"
                      className="block text-white/70 hover:text-[#FFD700] text-sm font-semibold transition-all"
                    >
                      +91-9451887217
                    </a>

                    <a
                      href="tel:+919450181867"
                      className="block text-white/70 hover:text-[#FFD700] text-sm font-semibold mt-0.5 transition-all"
                    >
                      +91-9450181867
                    </a>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FFD700] text-[#071A52] flex items-center justify-center shrink-0">
                    <Mail size={16} />
                  </div>

                  <a
                    href="mailto:psndwivedi@gmail.com"
                    className="text-white/70 hover:text-[#FFD700] text-sm font-semibold break-all transition-all"
                  >
                    psndwivedi@gmail.com
                  </a>
                </div>

                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FFD700] text-[#071A52] flex items-center justify-center shrink-0">
                    <Building2 size={16} />
                  </div>

                  <p className="text-white/70 text-xs leading-relaxed">
                    Higher Education Institution
                  </p>
                </div>

                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FFD700] text-[#071A52] flex items-center justify-center shrink-0">
                    <BookOpen size={16} />
                  </div>

                  <p className="text-white/70 text-xs leading-relaxed">
                    Courses: B.A. and B.Com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;